import type React from "react";
import { Template, ChartRow, FormState, ContentItem, RunRecord, MetricRow } from "./types";

// ── Palette (driven by CSS variables — see THEME_VARS below) ─────────────────
// Each token resolves at runtime via the [data-theme] attribute on the root wrapper.
export const C = {
  accent:       "var(--c-accent)",
  accentLight:  "var(--c-accent-light)",
  accentBorder: "var(--c-accent-border)",
  accentGlow:   "var(--c-accent-glow)",
  accentText:   "var(--c-accent-text)",
  bg:           "var(--c-bg)",
  surface:      "var(--c-surface)",
  border:       "var(--c-border)",
  borderHover:  "var(--c-border-hover)",
  text:         "var(--c-text)",
  muted:        "var(--c-muted)",
  subtle:       "var(--c-subtle)",
  faint:        "var(--c-faint)",
  success:      "var(--c-success)",
  successBg:    "var(--c-success-bg)",
  successBorder:"var(--c-success-border)",
  error:        "var(--c-error)",
  errorBg:      "var(--c-error-bg)",
  errorBorder:  "var(--c-error-border)",
  warn:         "var(--c-warn)",
  warnBg:       "var(--c-warn-bg)",
  warnBorder:   "var(--c-warn-border)",
  purple:       "var(--c-purple)",
  purpleBg:     "var(--c-purple-bg)",
  purpleBorder: "var(--c-purple-border)",
  panel:        "var(--c-panel)",
  modal:        "var(--c-modal)",
  tooltip:      "var(--c-tooltip)",
};

// ── Theme tokens ──────────────────────────────────────────────────────────────
const THEME_VARS = `
  :root, [data-theme="dark"] {
    --c-accent:        #8b5cf6;
    --c-accent-light:  rgba(139,92,246,0.10);
    --c-accent-border: rgba(139,92,246,0.22);
    --c-accent-glow:   rgba(139,92,246,0.28);
    --c-accent-text:   #c4b5fd;
    --c-bg:            #09090d;
    --c-surface:       rgba(255,255,255,0.04);
    --c-border:        rgba(255,255,255,0.08);
    --c-border-hover:  rgba(255,255,255,0.15);
    --c-text:          #f1f0f5;
    --c-muted:         #a1a1aa;
    /* #8a8a94, matching the prototype's --sub/--dim. It was #71717a, which is ~4.0:1 against this
       background — under the 4.5:1 body-text threshold, and the reason secondary labels read as
       "greyed out" rather than as text. #8a8a94 is ~5.6:1 and is what the prototype ships. */
    --c-subtle:        #8a8a94;
    /* Deliberately near-invisible: separators, disabled controls, placeholder dashes. Never text a
       reviewer has to READ — anything that has to be read is --c-subtle or lighter. */
    --c-faint:         #3f3f46;
    --c-success:       #4ade80;
    --c-success-bg:    rgba(74,222,128,0.09);
    --c-success-border:rgba(74,222,128,0.22);
    --c-error:         #f87171;
    --c-error-bg:      rgba(248,113,113,0.09);
    --c-error-border:  rgba(248,113,113,0.22);
    --c-warn:          #fbbf24;
    --c-warn-bg:       rgba(251,191,36,0.09);
    --c-warn-border:   rgba(251,191,36,0.22);
    --c-purple:        #c4b5fd;
    --c-purple-bg:     rgba(167,139,250,0.12);
    --c-purple-border: rgba(167,139,250,0.30);
    --c-scrollbar:     #2a2a30;
    --c-row-hover:     rgba(139,92,246,0.05);
    --c-row-divider:   rgba(255,255,255,0.05);
    --c-toast-bg:      #18181f;
    --c-option-bg:     #09090d;
    --c-color-scheme:  dark;
    --c-panel:         #0d0d12;
    --c-modal:         #111118;
    --c-tooltip:       #18181f;
    /* Lightness of the red→green tier-progress ramp (PerformanceView
       tierBarColor). Brighter on the dark ground, darker on light so the
       mid-ramp yellows stay readable — same idea as success/warn above. */
    --c-tier-ramp-lightness: 52%;
    --c-shadow-sm:     0 1px 3px rgba(0,0,0,0.3),0 1px 2px rgba(0,0,0,0.2);
    --c-shadow-md:     0 4px 16px rgba(0,0,0,0.35),0 1px 4px rgba(0,0,0,0.2);
    --c-shadow-lg:     0 8px 40px rgba(0,0,0,0.5),0 2px 8px rgba(0,0,0,0.25);
  }
  [data-theme="light"] {
    --c-accent:        #7c3aed;
    --c-accent-light:  rgba(124,58,237,0.08);
    --c-accent-border: rgba(124,58,237,0.22);
    --c-accent-glow:   rgba(124,58,237,0.16);
    --c-accent-text:   #5b21b6;
    --c-bg:            #fafafa;
    --c-surface:       rgba(0,0,0,0.025);
    --c-border:        rgba(0,0,0,0.09);
    --c-border-hover:  rgba(0,0,0,0.16);
    --c-text:          #18181b;
    --c-muted:         #52525b;
    --c-subtle:        #71717a;
    --c-faint:         #a1a1aa;
    --c-success:       #16a34a;
    --c-success-bg:    rgba(22,163,74,0.08);
    --c-success-border:rgba(22,163,74,0.25);
    --c-error:         #dc2626;
    --c-error-bg:      rgba(220,38,38,0.08);
    --c-error-border:  rgba(220,38,38,0.25);
    --c-warn:          #d97706;
    --c-warn-bg:       rgba(217,119,6,0.08);
    --c-warn-border:   rgba(217,119,6,0.25);
    --c-purple:        #6d28d9;
    --c-purple-bg:     rgba(109,40,217,0.08);
    --c-purple-border: rgba(109,40,217,0.25);
    --c-scrollbar:     #d4d4d8;
    --c-row-hover:     rgba(124,58,237,0.05);
    --c-row-divider:   rgba(0,0,0,0.05);
    --c-toast-bg:      #ffffff;
    --c-option-bg:     #ffffff;
    --c-color-scheme:  light;
    --c-panel:         #f4f4f5;
    --c-modal:         #ffffff;
    --c-tooltip:       #ffffff;
    --c-tier-ramp-lightness: 40%;
    --c-shadow-sm:     0 1px 3px rgba(0,0,0,0.08),0 1px 2px rgba(0,0,0,0.05);
    --c-shadow-md:     0 4px 16px rgba(0,0,0,0.10),0 1px 4px rgba(0,0,0,0.06);
    --c-shadow-lg:     0 8px 40px rgba(0,0,0,0.14),0 2px 8px rgba(0,0,0,0.08);
  }
`;

// ── Shared style objects ──────────────────────────────────────────────────────
export const LS: React.CSSProperties = {
  fontSize: 11, color: C.subtle, letterSpacing: "0.06em",
  textTransform: "uppercase", fontWeight: 600, display: "block", marginBottom: 7,
};
export const IS: React.CSSProperties = {
  width: "100%", background: "rgba(255,255,255,0.04)", border: `1px solid ${C.border}`,
  borderRadius: 8, color: C.text, fontSize: 13, padding: "9px 12px",
  outline: "none", fontFamily: "inherit", transition: "border-color 0.15s, box-shadow 0.15s",
};
export const CARD: React.CSSProperties = {
  background: C.surface, border: `1px solid ${C.border}`,
  borderRadius: 14, padding: "22px",
};

// ── Global CSS injected once at the root ─────────────────────────────────────
export const GLOBAL_CSS = `
  ${THEME_VARS}
  *{box-sizing:border-box;margin:0;padding:0;}
  html,body{height:100%;}
  body{
    font-family:'Inter',system-ui,-apple-system,BlinkMacSystemFont,sans-serif;
    font-feature-settings:"cv02","cv03","cv04","cv11";
    -webkit-font-smoothing:antialiased;
    -moz-osx-font-smoothing:grayscale;
    background:var(--c-bg);
    color:var(--c-text);
    font-size:14px;
    line-height:1.55;
  }
  h1,h2,h3,h4{
    font-family:'Plus Jakarta Sans','Inter',system-ui,sans-serif;
    letter-spacing:-0.025em;
    text-wrap:balance;
    line-height:1.2;
  }
  ::-webkit-scrollbar{width:5px;height:5px;}
  ::-webkit-scrollbar-track{background:transparent;}
  ::-webkit-scrollbar-thumb{background:var(--c-scrollbar);border-radius:10px;}
  ::-webkit-scrollbar-thumb:hover{background:var(--c-faint);}
  input[type=range]{-webkit-appearance:none;height:4px;background:var(--c-border);border-radius:4px;outline:none;width:100%;cursor:pointer;}
  input[type=range]::-webkit-slider-thumb{-webkit-appearance:none;width:16px;height:16px;border-radius:50%;background:var(--c-accent);cursor:pointer;border:2px solid var(--c-bg);box-shadow:0 0 0 3px var(--c-accent-border);transition:box-shadow 0.15s;}
  input[type=range]::-webkit-slider-thumb:hover{box-shadow:0 0 0 5px var(--c-accent-border);}
  .ni{display:flex;align-items:center;gap:9px;padding:8px 12px;border-radius:8px;cursor:pointer;color:var(--c-muted);font-size:13px;font-weight:500;border:none;background:none;width:100%;text-align:left;font-family:inherit;transition:all 0.15s;letter-spacing:-0.01em;}
  .ni:hover{background:var(--c-surface);color:var(--c-text);}
  .ni.on{background:var(--c-accent-light);color:var(--c-accent-text);outline:1px solid var(--c-accent-border);font-weight:600;}
  .sc{background:var(--c-surface);border:1px solid var(--c-border);border-radius:14px;padding:22px;transition:border-color 0.2s;box-shadow:var(--c-shadow-sm);}
  .sc:hover{border-color:var(--c-border-hover);}
  .tb{padding:6px 14px;border-radius:7px;border:none;cursor:pointer;font-size:13px;font-weight:500;font-family:inherit;transition:all 0.15s;background:transparent;color:var(--c-subtle);letter-spacing:-0.01em;}
  .tb.on{background:var(--c-accent-light);color:var(--c-accent-text);outline:1px solid var(--c-accent-border);}
  .tb:hover:not(.on){color:var(--c-muted);background:var(--c-surface);}
  .rb{padding:5px 13px;border-radius:7px;border:1px solid var(--c-border);background:transparent;color:var(--c-muted);font-size:12px;font-weight:500;cursor:pointer;font-family:inherit;transition:all 0.15s;}
  .rb:hover{border-color:var(--c-border-hover);color:var(--c-text);}
  .rb.on{background:var(--c-accent-light);color:var(--c-accent-text);border-color:var(--c-accent-border);}
  tr{border-bottom:1px solid var(--c-row-divider);}
  tr:last-child{border-bottom:none;}
  tr:hover td{background:var(--c-row-hover);}
  th{font-size:11px;color:var(--c-subtle);font-weight:600;letter-spacing:0.06em;text-transform:uppercase;padding:10px 14px;text-align:left;}
  td{padding:11px 14px;font-size:13px;font-variant-numeric:tabular-nums;}
  .ib{display:inline-flex;align-items:center;justify-content:center;width:30px;height:30px;border-radius:7px;border:none;background:transparent;color:var(--c-faint);cursor:pointer;transition:all 0.15s;}
  .ib:hover{background:var(--c-surface);color:var(--c-muted);}
  /* Icon button that must be legible AT REST, not only on hover: the default .ib sits on --c-faint,
     which is deliberately near-invisible and right for incidental affordances but wrong for a row's
     primary control. Lower specificity than .ib:hover, so the hover brighten still wins. */
  .ib-vis{color:var(--c-subtle);}
  /* Upload-queue row: rows are interactive (remove), so they need feedback that they are more than
     static text. Border only — a background change would fight the row's own status colour. */
  .qrow{transition:border-color 0.15s;}
  .qrow:hover{border-color:var(--c-border-hover);}
  .st{flex:1;padding:12px 8px;text-align:center;border-bottom:2px solid transparent;cursor:pointer;font-size:13px;font-weight:500;color:var(--c-subtle);background:none;border-top:none;border-left:none;border-right:none;font-family:inherit;transition:all 0.15s;}
  .st.on{border-bottom-color:var(--c-accent);color:var(--c-text);}
  .st.done{color:var(--c-muted);border-bottom-color:var(--c-accent-border);}
  .fi:focus{border-color:var(--c-accent)!important;box-shadow:0 0 0 3px var(--c-accent-light)!important;outline:none;}
  .ra{display:flex;gap:6px;opacity:0;transition:opacity 0.15s;}
  tr:hover .ra{opacity:1;}
  .co:hover{border-color:var(--c-accent)!important;box-shadow:0 0 0 2px var(--c-accent-light)!important;}
  .toast{position:fixed;bottom:24px;right:24px;background:var(--c-toast-bg);border:1px solid var(--c-accent-border);border-radius:12px;padding:14px 20px;display:flex;align-items:center;gap:10px;font-size:13px;font-weight:500;color:var(--c-accent-text);box-shadow:var(--c-shadow-lg);z-index:9999;animation:slideUp 0.35s cubic-bezier(0.34,1.56,0.64,1);}
  .modal-blur{backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px);}
  @keyframes slideUp{from{transform:translateY(16px);opacity:0}to{transform:translateY(0);opacity:1}}
  @keyframes slideIn{from{transform:translateX(100%);opacity:0}to{transform:translateX(0);opacity:1}}
  @keyframes blink{0%,100%{opacity:1}50%{opacity:0.3}}
  @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
  @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.3}}
  @keyframes fadeIn{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}}
  @media(max-width:880px){.sg{grid-template-columns:1fr 1fr!important;}}
  select{color-scheme:var(--c-color-scheme);}
  select option{background:var(--c-option-bg);color:var(--c-text);}
  input[type=date]{color-scheme:var(--c-color-scheme);}
  @media(prefers-reduced-motion:reduce){*{animation-duration:0.01ms!important;transition-duration:0.01ms!important;}}
`;

// ── Option lists ──────────────────────────────────────────────────────────────
export const NETWORKS        = ["amazon","impact","other"];
// Niche options — single source of truth, shared by upload modal + filters in
// Content/Launcher views. The value of `content.niche` is always one of these.
export const NICHE_OPTIONS   = [
  "Home & Kitchen",
  "Beauty & Skincare",
  "Fashion & Accessories",
  "Health & Wellness",
  "Fitness & Sports",
  "Tech & Gadgets",
  "Baby & Kids",
  "Pet Supplies",
  "Outdoor & Garden",
  "Travel & Luggage",
  "Food & Beverage",
  "Cleaning & Organization",
  "Office & Productivity",
  "Automotive",
  "Arts & Crafts",
  "Books & Education",
  "Toys & Games",
  "Jewelry & Watches",
  "Sustainable & Eco-Friendly",
  "Gifts & Novelty",
  "Deals",
  "Variety",
];
// Destination kinds and the merchant presets for a content asset's links. Here rather than in
// ContentLibraryView because the scraped-post review modal picks from the same two lists, and a
// second copy is how the upload form and the review form start disagreeing. The destination kinds
// must stay in step with `MerchantUrl["destination_type"]` in types.ts and with DESTINATION_TYPES
// in socialiq_content/core/social_crawl/types.py, which refuses anything outside the list.
export const DESTINATION_TYPES = ["storefront","collection","idealist","product_page","search_page"];
// Presets only — a creator's own merchants are fetched and merged in on top of these.
export const MERCHANTS_PRESETS = ["Amazon","Walmart","Home Depot","Target"];
export const MEDIA_TYPES     = ["IMAGE","VIDEO"];
export const AD_TYPES        = ["partnership","normal"];
export const CREATIVE_TYPES  = ["static","carousel"];
export const CAMPAIGN_OPT    = ["traffic","sales"];
export const GENDERS         = ["all","male","female"];
export const DESTINATIONS    = ["idealist","collection","storefront","product_page","search_page"];
export const COUNTRIES       = ["US"];
export const PLATFORMS       = ["FB"];
export const BEAST_QUOTES    = [
  "Feed me your content.",
  "I hunger for impressions.",
  "Give me audiences to devour.",
  "More. Always more.",
  "I consume. I convert. I conquer.",
];
// CREATORS_HIDDEN: export const WIZARD_STEPS = ["Identity","Targeting","Creative","Content","Creators","Review"];
export const WIZARD_STEPS    = ["Identity","Targeting","Creative","Content","Review"];

// ── Default form ──────────────────────────────────────────────────────────────
export const EMPTY_FORM: FormState = {
  name: "",
  ts_id: "FB", account_id: "", a_id: "", n_ads: 1, country_code: "US",
  // media_type removed — comes from uploaded content
  ad_type: "partnership", creative_type: "static",
  campaign_optimization: "traffic", gender: "female", min_age: 18, max_age: 65,
  network: "amazon", program_id: "", destination: "idealist",
  content: "provided", product_category: "", active: true,
  is_social_deeplink: false,
  deeplink_provider: "joylink",
  selected_creators: [],
  budget: 15,
  campaign_id: "",
  adset_id: "",
  tracking_link: "",
  page_id: "",
  sponsor_page_id: "",
  pixel_id: "",
  placements: {},
};

// ── Meta ad placements — full canonical list per platform ────────────────────
// Used by the WizardView step 2 placement picker. Keep keys aligned with the
// `publisher_platforms` and `*_positions` arrays sent to the Marketing API.
export const DEVICE_PLATFORM_OPTIONS: { value: "mobile" | "desktop"; label: string }[] = [
  { value: "mobile",  label: "Mobile" },
  { value: "desktop", label: "Desktop" },
];

export const MOBILE_OS_OPTIONS: { value: "iOS" | "Android"; label: string }[] = [
  { value: "iOS",     label: "iOS" },
  { value: "Android", label: "Android" },
];

export const PLACEMENT_OPTIONS: { platform: "facebook"|"instagram"|"messenger"|"audience_network"|"threads"; label: string; positions: { value: string; label: string }[] }[] = [
  {
    platform: "facebook",
    label: "Facebook",
    positions: [
      { value: "feed",                    label: "Feed" },
      { value: "story",                   label: "Stories" },
      { value: "video_feeds",             label: "Video feeds" },
      { value: "facebook_reels",          label: "Reels" },
      { value: "facebook_reels_overlay",  label: "Reels overlay" },
      { value: "instream_video",          label: "In-stream video" },
      { value: "right_hand_column",       label: "Right-hand column" },
      { value: "marketplace",             label: "Marketplace" },
      { value: "search",                  label: "Search results" },
      { value: "groups_feed",             label: "Groups feed" },
    ],
  },
  {
    platform: "instagram",
    label: "Instagram",
    positions: [
      { value: "stream",        label: "Feed" },
      { value: "story",         label: "Stories" },
      { value: "reels",         label: "Reels" },
      { value: "explore",       label: "Explore" },
      { value: "explore_home",  label: "Explore home" },
      { value: "profile_feed",  label: "Profile feed" },
      { value: "ig_search",     label: "Search results" },
    ],
  },
  {
    platform: "messenger",
    label: "Messenger",
    positions: [
      { value: "messenger_home",     label: "Inbox" },
      { value: "story",              label: "Stories" },
      { value: "sponsored_messages", label: "Sponsored messages" },
    ],
  },
  {
    platform: "audience_network",
    label: "Audience Network",
    positions: [
      { value: "classic",        label: "Native, banner, interstitial" },
      { value: "rewarded_video", label: "Rewarded videos" },
    ],
  },
  {
    platform: "threads",
    label: "Threads",
    positions: [
      { value: "threads_stream", label: "Threads feed" },
    ],
  },
];

// ── Mock data (shown when no Retool data bound) ───────────────────────────────
export const MOCK_TEMPLATES: Template[] = [
  { id:1, ts_id:"FB", account_id:"357", a_id:"165", n_ads:5,  country_code:"US", ad_type:"partnership", creative_type:"static",       campaign_optimization:"traffic",     gender:"female", min_age:40, max_age:65, network:"amazon",    destination:"idealist",     content:"provided", active:true,  created:"2026-02-17", last_run:"2026-02-24 06:00", run_count:12, status:"success", is_social_deeplink:false },
  { id:2, ts_id:"IG", account_id:"412", a_id:"201", n_ads:3,  country_code:"CA", ad_type:"branded",     creative_type:"ugc",          campaign_optimization:"conversions", gender:"all",    min_age:25, max_age:45, network:"amazon",    destination:"storefront",    content:"research", active:true,  created:"2026-02-10", last_run:"2026-02-23 06:00", run_count:7,  status:"success", is_social_deeplink:false },
  { id:3, ts_id:"TK", account_id:"889", a_id:"330", n_ads:8,  country_code:"US", ad_type:"conversion",  creative_type:"ai-generated", campaign_optimization:"reach",       gender:"male",   min_age:18, max_age:35, network:"impact",    destination:"product_page", content:"research", active:false, created:"2026-01-30", last_run:"2026-02-20 06:00", run_count:3,  status:"error",   is_social_deeplink:true  },
  { id:4, ts_id:"FB", account_id:"357", a_id:"177", n_ads:2,  country_code:"US", ad_type:"awareness",   creative_type:"static",       campaign_optimization:"engagement",  gender:"female", min_age:30, max_age:55, network:"amazon",    destination:"idealist",     content:"provided", active:true,  created:"2026-02-01", last_run:"2026-02-24 06:00", run_count:18, status:"success", is_social_deeplink:false },
  { id:5, ts_id:"FB", account_id:"501", a_id:"098", n_ads:1,  country_code:"US", ad_type:"partnership", creative_type:"ugc",          campaign_optimization:"traffic",     gender:"all",    min_age:20, max_age:50, network:"impact",    destination:"idealist",     content:"provided", active:true,  created:"2026-02-14", run_count:0, status:"pending",         is_social_deeplink:false },
];

export const MOCK_CHART: ChartRow[] = [
  { date:"Feb 10", ads:12, conversions:4  },
  { date:"Feb 14", ads:8,  conversions:3  },
  { date:"Feb 16", ads:27, conversions:14 },
  { date:"Feb 18", ads:22, conversions:11 },
  { date:"Feb 20", ads:35, conversions:18 },
  { date:"Feb 22", ads:29, conversions:13 },
  { date:"Feb 24", ads:42, conversions:22 },
];

export const MOCK_CONTENT: ContentItem[] = [
  // Creator 165 — provided
  {
    id:1, a_id:"165", creator_name:"Sarah M.", filename:"hero-shot-v1.jpg", type:"image", source:"provided", status:"approved", created:"2026-02-20", size_kb:340,
    url:"https://cdn.pixabay.com/photo/2023/04/06/09/39/camera-7903435_1280.jpg",
    niche:"Lifestyle & gifting",
    seasonal:true, start_date:"2026-11-01", end_date:"2026-12-31",
    requires_starting_hour:true, starting_hour:9,
    merchant_urls:[
      { merchant:"Amazon",            url:"https://www.amazon.com/vdp/07dc9d4122fd", destination_type:"idealist" },
      { merchant:"WalmartCreator.com", url:"https://www.walmart.com/creator/posts?creator=sarahm", destination_type:"storefront" },
    ],
  },
  {
    id:2, a_id:"165", creator_name:"Sarah M.", filename:"unboxing-60s.mp4", type:"video", source:"provided", status:"approved", created:"2026-02-18", size_kb:8200,
    url:"https://cdn.pixabay.com/video/2026/01/28/330898_large.mp4",
    audio_tag:"royalty_free",
    merchant_urls:[{ merchant:"Amazon", url:"https://www.amazon.com/vdp/abc123", destination_type:"idealist" }],
  },
  {
    id:3, a_id:"165", creator_name:"Sarah M.", filename:"lifestyle-batch.jpg", type:"image", source:"provided", status:"for_review", created:"2026-02-23", size_kb:280,
    url:"https://cdn.pixabay.com/photo/2016/07/30/21/38/mario-1558068_1280.jpg",
    merchant_urls:[],
  },
  // Creator 165 — generated
  {
    id:4, a_id:"165", creator_name:"Sarah M.", filename:"gen-carousel-3up.jpg", type:"image", source:"generated", status:"approved", created:"2026-02-22", size_kb:1100,
    url:"https://cdn.pixabay.com/photo/2014/04/09/10/31/macaw-320006_1280.jpg",
    merchant_urls:[{ merchant:"Amazon", url:"https://www.amazon.com/vdp/xyz789", destination_type:"collection" }],
  },
  {
    id:5, a_id:"165", creator_name:"Sarah M.", filename:"gen-static-v2.jpg", type:"image", source:"generated", status:"for_internal_review", created:"2026-02-24", size_kb:420,
    url:"https://cdn.pixabay.com/photo/2026/01/29/18/52/parrot-10094674_1280.jpg",
    merchant_urls:[],
  },
  {
    id:6, a_id:"165", creator_name:"Sarah M.", filename:"gen-reel-15s.mp4", type:"video", source:"generated", status:"for_review", created:"2026-02-24", size_kb:3200,
    url:"https://cdn.pixabay.com/video/2018/02/09/14205-255658030_large.mp4",
    audio_tag:"no_sound",
    merchant_urls:[],
  },
  // Creator 201 — provided
  {
    id:7, a_id:"201", creator_name:"James K.", filename:"product-flat-lay.jpg", type:"image", source:"provided", status:"approved", created:"2026-02-15", size_kb:510,
    url:"https://cdn.pixabay.com/photo/2026/01/29/18/52/parrot-10094674_1280.jpg",
    merchant_urls:[{ merchant:"Amazon", url:"https://www.amazon.com/vdp/james01", destination_type:"idealist" }],
  },
  {
    id:8, a_id:"201", creator_name:"James K.", filename:"review-video.mp4", type:"video", source:"provided", status:"for_review", created:"2026-02-21", size_kb:6400,
    url:"https://cdn.pixabay.com/video/2018/02/09/14205-255658030_large.mp4",
    audio_tag:"copyrighted_audio",
    merchant_urls:[],
  },
  // Creator 201 — generated
  {
    id:9,  a_id:"201", creator_name:"James K.", filename:"gen-banner-v1.jpg",     type:"image", source:"generated", status:"approved",           created:"2026-02-20", size_kb:380,  url:"https://cdn.pixabay.com/photo/2026/01/29/18/52/parrot-10094674_1280.jpg", merchant_urls:[{ merchant:"Amazon", url:"https://www.amazon.com/vdp/james02", destination_type:"product_page" }] },
  {
    id:10, a_id:"201", creator_name:"James K.", filename:"gen-story-format.jpg", type:"image", source:"generated", status:"for_internal_review", created:"2026-02-25", size_kb:290,  url:"https://cdn.pixabay.com/photo/2026/01/29/18/52/parrot-10094674_1280.jpg", merchant_urls:[] },
  // Creator 330
  {
    id:11, a_id:"330", creator_name:"Mia Chen", filename:"tiktok-raw-45s.mp4",  type:"video", source:"provided",  status:"approved",           created:"2026-02-19", size_kb:9100, url:"https://cdn.pixabay.com/video/2018/02/09/14205-255658030_large.mp4", audio_tag:"royalty_free", merchant_urls:[{ merchant:"Amazon", url:"https://www.amazon.com/vdp/mia01", destination_type:"idealist" }] },
  {
    id:12, a_id:"330", creator_name:"Mia Chen", filename:"gen-thumb-v3.jpg",    type:"image", source:"generated", status:"for_internal_review", created:"2026-02-26", size_kb:210,  url:"https://cdn.pixabay.com/photo/2026/01/29/18/52/parrot-10094674_1280.jpg", merchant_urls:[] },
];

export const MOCK_RUNS: RunRecord[] = [
  { id:1, template_id:1, ts_id:"FB", n_ads:5, network:"amazon",  status:"success", ran_at:"2026-02-24 06:00", duration_ms:1240, a_id:"165", creator_name:"Sarah M." },
  { id:2, template_id:4, ts_id:"FB", n_ads:2, network:"amazon",  status:"success", ran_at:"2026-02-24 06:00", duration_ms:980,  a_id:"177", creator_name:"Alex R."  },
  { id:3, template_id:3, ts_id:"TK", n_ads:8, network:"impact",  status:"error",   ran_at:"2026-02-23 06:00", duration_ms:3100, a_id:"330", creator_name:"Mia Chen",  error_msg:"API rate limit exceeded" },
  { id:4, template_id:2, ts_id:"IG", n_ads:3, network:"amazon",  status:"success", ran_at:"2026-02-23 06:00", duration_ms:870,  a_id:"201", creator_name:"James K." },
  { id:5, template_id:1, ts_id:"FB", n_ads:5, network:"amazon",  status:"success", ran_at:"2026-02-23 06:00", duration_ms:1180, a_id:"165", creator_name:"Sarah M." },
];

export const MOCK_METRICS: MetricRow[] = [
  { date:"Feb 10", impressions:12400, clicks:340, conversions:28, spend:42.5  },
  { date:"Feb 12", impressions:9800,  clicks:210, conversions:14, spend:31.0  },
  { date:"Feb 14", impressions:18600, clicks:520, conversions:47, spend:68.2  },
  { date:"Feb 16", impressions:22100, clicks:680, conversions:61, spend:89.4  },
  { date:"Feb 18", impressions:19300, clicks:590, conversions:53, spend:74.1  },
  { date:"Feb 20", impressions:31200, clicks:920, conversions:88, spend:121.3 },
  { date:"Feb 22", impressions:28700, clicks:840, conversions:79, spend:108.7 },
  { date:"Feb 24", impressions:35100, clicks:1040,conversions:101,spend:142.6 },
];
